import type { AppError } from '@/lib/api/types/errors';
import { ErrorType } from '@/lib/api/types/errors';
import { generateSuccessMessage } from '@/lib/notifications/message-generator';
import { showErrorToastFromError, showSuccessToast } from '@/shared/notifications/toast-utils';
import type { AnalyticsQuery } from '@skewb-works/contracts';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import type { MutationMeta } from './query-client/types';

/**
 * Query Client Configuration
 *
 * Centralized configuration for TanStack Query with global error handling,
 * retry logic, and caching strategies.
 */

/**
 * Determines if an error should be retried
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  // Don't retry after 3 attempts
  if (failureCount >= 3) {
    return false;
  }

  // Don't retry client errors (4xx) except 408 (timeout)
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const statusCode = (error as AppError).statusCode;
    if (statusCode && statusCode >= 400 && statusCode < 500 && statusCode !== 408) {
      return false;
    }
  }

  // Don't retry authentication or authorization errors
  if (error && typeof error === 'object' && 'type' in error) {
    const errorType = (error as AppError).type;
    if (errorType === ErrorType.AUTHENTICATION || errorType === ErrorType.AUTHORIZATION) {
      return false;
    }
  }

  // Retry network errors and server errors
  return true;
}

/**
 * Global error handler for queries
 */
function _handleQueryError(error: unknown): void {
  // Don't show toast for every error - let components handle specific ones
  // Only show toasts for critical errors
  if (error && typeof error === 'object' && 'type' in error) {
    const appError = error as AppError;

    // Show toast for network errors
    if (appError.type === ErrorType.NETWORK) {
      showErrorToastFromError(appError);
      return;
    }

    // Show toast for authentication errors
    if (appError.type === ErrorType.AUTHENTICATION) {
      showErrorToastFromError(appError);
      // Could redirect to login here
      return;
    }

    // Log other errors but don't show toast (let component handle it)
    if (import.meta.env.DEV) {
      console.error('Query error:', appError);
    }
  }
}

/**
 * Global success handler for mutations
 * Automatically shows success toast based on mutation metadata
 */
function handleMutationSuccess(
  data: unknown,
  variables: unknown,
  context: unknown,
  mutation: any,
): void {
  const meta = mutation.options.meta as MutationMeta | undefined;

  // Skip if explicitly disabled
  if (meta?.skipSuccessToast) {
    return;
  }

  // Try to generate message from metadata
  const message = meta ? generateSuccessMessage(meta, variables) : null;

  if (message) {
    showSuccessToast(message.title, message.description, meta?.successDuration);
  }

  // Log in dev mode
  if (import.meta.env.DEV) {
    console.log('Mutation success:', {
      meta,
      variables,
      data,
    });
  }
}

/**
 * Global error handler for mutations
 */
function handleMutationError(
  error: unknown,
  variables: unknown,
  context: unknown,
  mutation: any,
): void {
  const meta = mutation.options.meta as MutationMeta | undefined;

  // Skip if explicitly disabled
  if (meta?.skipErrorToast) {
    return;
  }

  // Show toast for all mutation errors since they're user-initiated actions
  if (error && typeof error === 'object') {
    showErrorToastFromError(error as AppError, meta?.errorTitle);
  } else {
    showErrorToastFromError(new Error('An unexpected error occurred'), meta?.errorTitle);
  }

  if (import.meta.env.DEV) {
    console.error('Mutation error:', error);
  }
}

/**
 * Create and configure the Query Client
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: handleMutationSuccess,
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      // Stale time: Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Cache time: Unused data stays in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry configuration
      retry: shouldRetry,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch on window focus by default
      refetchOnWindowFocus: false,

      // Refetch on mount only if data is stale
      refetchOnMount: true,

      // Don't refetch on reconnect by default (too aggressive)
      refetchOnReconnect: false,

      // Global error handler
      // Note: This is called after retry attempts are exhausted
      throwOnError: false, // Let components handle errors
    },

    mutations: {
      // Retry mutations once for network/server errors
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;

        if (error && typeof error === 'object' && 'type' in error) {
          const appError = error as AppError;
          // Only retry network and server errors
          return (
            appError.type === ErrorType.NETWORK ||
            appError.type === ErrorType.SERVER ||
            appError.type === ErrorType.TIMEOUT
          );
        }

        return false;
      },
    },
  },
});

// Re-export types for convenience
export type { MutationMeta } from './query-client/types';
export { generateSuccessMessage } from '@/lib/notifications/message-generator';

/**
 * Query Keys Factory
 *
 * Centralized query key management for type safety and consistency
 */
export const queryKeys = {
  // Jobs
  jobs: {
    all: ['jobs'] as const,
    lists: () => [...queryKeys.jobs.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.jobs.lists(), filters] as const,
    details: () => [...queryKeys.jobs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.jobs.details(), id] as const,
    jobTypes: () => [...queryKeys.jobs.all, 'jobTypes'] as const,
    priorities: () => [...queryKeys.jobs.all, 'priorities'] as const,
    workOrderStatuses: () => [...queryKeys.jobs.all, 'workOrderStatuses'] as const,
    permitStatuses: () => [...queryKeys.jobs.all, 'permitStatuses'] as const,
    counts: () => [...queryKeys.jobs.all, 'counts'] as const,
    activityLog: (id: string) => [...queryKeys.jobs.all, 'activityLog', id] as const,
    documents: (id: string) => [...queryKeys.jobs.all, 'documents', id] as const,
    dataCapture: (id: string) => [...queryKeys.jobs.all, 'dataCapture', id] as const,
    dfe: (id: string) => [...queryKeys.jobs.all, 'dfe', id] as const,
  },

  // Work Packages
  workPackages: {
    all: ['work-packages'] as const,
    lists: () => [...queryKeys.workPackages.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.workPackages.lists(), filters] as const,
    details: () => [...queryKeys.workPackages.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workPackages.details(), id] as const,
  },

  // Teams
  teams: {
    all: ['teams'] as const,
    lists: () => [...queryKeys.teams.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.teams.lists(), filters] as const,
    details: () => [...queryKeys.teams.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.teams.details(), id] as const,
  },

  // Shifts
  shifts: {
    all: ['shifts'] as const,
    lists: () => [...queryKeys.shifts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.shifts.lists(), filters] as const,
    details: () => [...queryKeys.shifts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.shifts.details(), id] as const,
    teamShifts: (teamId: number) => [...queryKeys.shifts.all, 'team', teamId] as const,
  },

  // Planner
  planner: {
    all: ['planner'] as const,
    resources: (teamId?: number) => [...queryKeys.planner.all, 'resources', teamId] as const,
    events: (start: string, end: string, teamId?: number) =>
      [...queryKeys.planner.all, 'events', start, end, teamId] as const,
    availability: (start: string, end: string, userIds?: number[]) =>
      [...queryKeys.planner.all, 'availability', start, end, userIds] as const,
    capacity: (start: string, end: string, userIds?: number[]) =>
      [...queryKeys.planner.all, 'capacity', start, end, userIds] as const,
    resourceAvailability: (start: string, end: string, userIds?: number[]) =>
      [...queryKeys.planner.all, 'resourceAvailability', start, end, userIds] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    counts: () => [...queryKeys.users.all, 'counts'] as const,
    verificationStats: () => [...queryKeys.users.all, 'verificationStats'] as const,
    lineManagers: (search?: string) => [...queryKeys.users.all, 'lineManagers', search] as const,
    activity: (id: string, filters?: Record<string, unknown>) =>
      [...queryKeys.users.all, 'activity', id, filters] as const,
    roles: (id: string) => [...queryKeys.users.all, 'roles', id] as const,
  },

  // Settings — Job Management
  settings: {
    all: ['settings'] as const,
    jobCategories: () => [...queryKeys.settings.all, 'jobCategories'] as const,
    taskTypes: () => [...queryKeys.settings.all, 'taskTypes'] as const,
    categoryTasks: (categoryId: number) =>
      [...queryKeys.settings.all, 'categoryTasks', categoryId] as const,
    jobTitles: () => [...queryKeys.settings.all, 'jobTitles'] as const,
    disciplines: () => [...queryKeys.settings.all, 'disciplines'] as const,
    skills: () => [...queryKeys.settings.all, 'skills'] as const,
    leaveTypes: () => [...queryKeys.settings.all, 'leaveTypes'] as const,
  },
  locations: {
    all: ['locations'] as const,
    regions: () => [...queryKeys.locations.all, 'regions'] as const,
    areas: () => [...queryKeys.locations.all, 'areas'] as const,
  },

  // Audit Trail
  auditTrail: {
    all: ['audit-trail'] as const,
    entity: (entityType: string, entityId: string) =>
      ['audit-trail', 'entity', entityType, entityId] as const,
    search: (filters?: Record<string, unknown>) => ['audit-trail', 'search', filters] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    kpis: (filters?: AnalyticsQuery) => [...queryKeys.dashboard.all, 'kpis', filters] as const,
    jobs: (filters?: AnalyticsQuery) => [...queryKeys.dashboard.all, 'jobs', filters] as const,
    performance: (filters?: AnalyticsQuery) =>
      [...queryKeys.dashboard.all, 'performance', filters] as const,
    jobCategories: () => [...queryKeys.dashboard.all, 'jobCategories'] as const,
    users: () => [...queryKeys.dashboard.all, 'users'] as const,
    priorities: () => [...queryKeys.dashboard.all, 'priorities'] as const,
  },
};
